/**
 * Undo and redo for the Composer (Dax, 2026-10-04), as arithmetic. The history holds whole snapshots of a
 * draft's editable state, each a few kilobytes of JSON, so a generous depth costs nothing: `present` is the
 * state on screen, `past` what undo returns to (oldest first), `future` what redo returns to (next last).
 * Pure, so the stacks are tested; use-draft-history.ts decides when a state has settled enough to record.
 */
export interface History { past: string[]; present: string | null; future: string[] }

export const EMPTY_HISTORY: History = { past: [], present: null, future: [] };

/**
 * A settled state. A new one becomes the present, the old present goes on the undo stack (the oldest falls
 * off past `limit`) and redo is forgotten. `adopt` takes it as the present without a step of its own: the
 * first state of a draft, or the follow-ups an undo sets off (levelling, frame quantising).
 */
export function record(h: History, snap: string, limit: number, adopt = false): History {
  if (snap === h.present) return h;
  if (adopt || h.present === null) return { ...h, present: snap };
  return { past: [...h.past, h.present].slice(-limit), present: snap, future: [] };
}

/** One step back: the state to restore, or null with nothing to undo. */
export function undo(h: History): { history: History; restore: string } | null {
  const restore = h.past.at(-1);
  if (restore === undefined || h.present === null) return null;
  return { history: { past: h.past.slice(0, -1), present: restore, future: [...h.future, h.present] }, restore };
}

/** One step forward again: the state to restore, or null with nothing to redo. */
export function redo(h: History): { history: History; restore: string } | null {
  const restore = h.future.at(-1);
  if (restore === undefined || h.present === null) return null;
  return { history: { past: [...h.past, h.present], present: restore, future: h.future.slice(0, -1) }, restore };
}
